import { definedMap } from "@glideapps/ts-necessities";
import md5 from "blueimp-md5";
import { withTimeout } from "../../../support/timeout";
import { actionRunTimeout } from "../../action-timeout";
import { callCloudFunction } from "../../call-cloud-function";
import type { DocumentData, RowIndex } from "../../Database";
import { documentIDColumnName, postDeleteRowAction } from "../../Database";
import type { TableName } from "../../database-strings";
import { commentsTableName, makeCommentsPath } from "../../database-strings";
import { logError, logInfo } from "../../debug-print";
import { makeTableName } from "../../description";
import { extractDropData } from "../../drop-controller";
import type {
    BaseRowIndex,
    ReportAppBody,
    SendAppFeedbackBody,
    TriggerAppWebhookActionBody,
    TriggerZapBody,
    WebhookValues,
} from "../../firebase-function-types";
import { checkBaseRowIndex } from "../../firebase-function-types";
import { supportsOfflineActionQueue } from "../../offline-action-queue";
import { Sound } from "../../sound";
import {
    assert,
    checkBoolean,
    checkString,
    defined,
    dontAwait,
    isArray,
    isEmptyOrUndefinedish,
    mapFilterUndefined,
    normalizeEmailAddress,
    panic,
    safeURL,
} from "../../Support";
import {
    asArrayValue,
    asBasePrimitiveValue,
    asBoolean,
    asNumber,
    asString,
    evaluateStringArray,
    getObjectInspectorFromValueProducer,
    getValueFromValueProducer,
    isLoadedDefined,
    loadedDefinedMap,
    makeWebhookValue,
    waitForValueFromValueProducer,
} from "../component-helpers";
import type { DeserializationSpec, Loading } from "../computation-types";
import { ActionSource, LOADING } from "../computation-types";
import type { ConfirmModalStyle } from "../confirm-modal";
import { showConfirmModal } from "../confirm-modal";
import { Data, waitForValueWithTimeout } from "../data";
import { registerActionDeserializable } from "../deserialization";
import { reshuffleKeys } from "../functions";
import { getLocalizedString } from "../localization";
import type { ScreenFlag } from "../SerializedApp";
import { getEmailClient } from "../SerializedApp";
import type {
    AppEnvironment,
    AppOther,
    Context,
    DataStore,
    EnvironmentBoundEvaluable,
    ForceValueProducer,
    MapLocation,
    PotentialValue,
    RenderEnvironment,
    Value,
} from "../types";
import {
    Action,
    ActionResult,
    asConditionVariable,
    isUploadFileResponseError,
    MapLocationKind,
    OnlineOnlyAction,
} from "../types";
import { uploadFileIntoGlideStorage } from "../upload-handlers";
import { DeliverEmailAction } from "./deliver-email";
import { evalAction, evalAndRunAction } from "./support";

type EvaluablesToValues<T extends readonly (EnvironmentBoundEvaluable | undefined)[]> = {
    [P in keyof T]: PotentialValue;
};

async function evalAndWaitForValue(
    ctx: Context,
    actionSource: ActionSource,
    evaluable: EnvironmentBoundEvaluable | undefined
): Promise<PotentialValue> {
    if (evaluable === undefined) return undefined;
    return await waitForValueWithTimeout(evaluable.eval(ctx, actionSource), 100);
}

async function evalAndWaitForValues<T extends readonly (EnvironmentBoundEvaluable | undefined)[]>(
    ctx: Context,
    actionSource: ActionSource,
    evaluables: T
): Promise<EvaluablesToValues<T>> {
    const promises = evaluables.map(e => evalAndWaitForValue(ctx, actionSource, e));
    return (await Promise.all(promises)) as unknown as EvaluablesToValues<T>;
}

class NoAction extends Action {
    public async run(): Promise<ActionResult | undefined> {
        return undefined;
    }

    public get isNoAction(): boolean {
        return true;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["NoAction", "action"];
    }
}

class HoldAction extends Action {
    constructor(private readonly _action: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource,
        handled?: boolean
    ): Promise<ActionResult | undefined> {
        return evalAndRunAction(ctx, env, actionSource, this._action, handled);
    }

    public canExecute(ctx: Context, force: ForceValueProducer): boolean {
        const action = evalAction(ctx, ActionSource.None, this._action);
        return isLoadedDefined(action) && action.canExecute(ctx, force);
    }

    public needsOnline(ctx: Context, force: ForceValueProducer, env: RenderEnvironment): [boolean, boolean] {
        const action = evalAction(ctx, ActionSource.None, this._action);
        if (!isLoadedDefined(action)) return [false, false];
        return action.needsOnline(ctx, force, env);
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["HoldAction", "action", ["action", "action"]];
    }
}

class SequenceAction extends Action {
    constructor(
        private readonly _first: EnvironmentBoundEvaluable,
        private readonly _second: EnvironmentBoundEvaluable,
        private readonly _third: EnvironmentBoundEvaluable | undefined,
        private readonly _fourth: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    public canExecute(ctx: Context, force: ForceValueProducer): boolean {
        const first = this.getFirstAction(ctx, ActionSource.None);
        const second = this.getSecondAction(ctx, ActionSource.None);
        const third = this.getThirdAction(ctx, ActionSource.None);
        const fourth = this.getFourthAction(ctx, ActionSource.None);

        if (isLoadedDefined(first) && first.canExecute(ctx, force)) return true;
        if (isLoadedDefined(second) && second.canExecute(ctx, force)) return true;
        if (isLoadedDefined(third) && third.canExecute(ctx, force)) return true;
        if (isLoadedDefined(fourth) && fourth.canExecute(ctx, force)) return true;

        return false;
    }

    public needsOnline(ctx: Context, force: ForceValueProducer, env: RenderEnvironment): [boolean, boolean] {
        let mutatesData = false;
        let reallyNeedsOnline = false;

        function add(action: Action | Loading | undefined): void {
            if (!isLoadedDefined(action)) return;
            const [m, r] = action.needsOnline(ctx, force, env);
            mutatesData = mutatesData || m;
            reallyNeedsOnline = reallyNeedsOnline || r;
        }

        add(this.getFirstAction(ctx, ActionSource.None));
        add(this.getSecondAction(ctx, ActionSource.None));
        add(this.getThirdAction(ctx, ActionSource.None));
        add(this.getFourthAction(ctx, ActionSource.None));

        return [mutatesData, reallyNeedsOnline];
    }

    private getFirstAction(ctx: Context, actionSource: ActionSource): Action | Loading | undefined {
        return evalAction(ctx, actionSource, this._first);
    }

    private getSecondAction(ctx: Context, actionSource: ActionSource): Action | Loading | undefined {
        return evalAction(ctx, actionSource, this._second);
    }

    private getThirdAction(ctx: Context, actionSource: ActionSource): Action | Loading | undefined {
        return evalAction(ctx, actionSource, this._third);
    }

    private getFourthAction(ctx: Context, actionSource: ActionSource): Action | Loading | undefined {
        return evalAction(ctx, actionSource, this._fourth);
    }

    public targetURL(ctx: Context, env: RenderEnvironment, actionSource: ActionSource): string | undefined {
        let url = loadedDefinedMap(this.getFirstAction(ctx, actionSource), a => a.targetURL(ctx, env, actionSource));
        if (isLoadedDefined(url)) return url;

        url = loadedDefinedMap(this.getSecondAction(ctx, actionSource), a => a.targetURL(ctx, env, actionSource));
        if (isLoadedDefined(url)) return url;

        url = loadedDefinedMap(this.getThirdAction(ctx, actionSource), a => a.targetURL(ctx, env, actionSource));
        if (isLoadedDefined(url)) return url;

        url = loadedDefinedMap(this.getFourthAction(ctx, actionSource), a => a.targetURL(ctx, env, actionSource));
        if (isLoadedDefined(url)) return url;

        return undefined;
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource,
        handled?: boolean
    ): Promise<ActionResult | undefined> {
        let result: ActionResult | undefined;
        const first = this.getFirstAction(ctx, actionSource);
        if (isLoadedDefined(first)) {
            result = await first.run(ctx, env, actionSource, handled);
        }

        const second = this.getSecondAction(ctx, actionSource);
        if (isLoadedDefined(second)) {
            result = await second.run(ctx, env, actionSource, handled);
        }

        const third = this.getThirdAction(ctx, actionSource);
        if (isLoadedDefined(third)) {
            result = await third.run(ctx, env, actionSource, handled);
        }

        const fourth = this.getFourthAction(ctx, actionSource);
        if (isLoadedDefined(fourth)) {
            result = await fourth.run(ctx, env, actionSource, handled);
        }

        return result;
    }

    // FIXME: Implement `isNoAction`

    static get spec(): DeserializationSpec<"action"> {
        return [
            "Sequence",
            "action",
            ["first", "action"],
            ["second", "action"],
            ["third", "action", false],
            ["fourth", "action", false],
        ];
    }
}

class AlertAction extends Action {
    constructor(private readonly _message: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const message = await evalAndWaitForValue(ctx, actionSource, this._message);
        if (!isLoadedDefined(message)) return undefined;

        logInfo("Alert", message);
        env.appEnvironment.appFacilities.alert(asString(message));

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["Alert", "action", ["message", "evaluable"]];
    }
}

class ZapierAction extends OnlineOnlyAction {
    constructor(
        private readonly _zapID: EnvironmentBoundEvaluable,
        private readonly _val1: EnvironmentBoundEvaluable,
        private readonly _val2: EnvironmentBoundEvaluable,
        private readonly _val3: EnvironmentBoundEvaluable,
        private readonly _val4: EnvironmentBoundEvaluable | undefined,
        private readonly _val5: EnvironmentBoundEvaluable | undefined,
        private readonly _val6: EnvironmentBoundEvaluable | undefined,
        private readonly _val7: EnvironmentBoundEvaluable | undefined,
        private readonly _val8: EnvironmentBoundEvaluable | undefined,
        private readonly _val9: EnvironmentBoundEvaluable | undefined,
        private readonly _val10: EnvironmentBoundEvaluable | undefined,
        private readonly _val11: EnvironmentBoundEvaluable | undefined,
        private readonly _val12: EnvironmentBoundEvaluable | undefined,
        private readonly _onSuccess: EnvironmentBoundEvaluable | undefined,
        private readonly _onFailure: EnvironmentBoundEvaluable | undefined,
        private readonly _onQuotaReached: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const zapID = getValueFromValueProducer(this._zapID.eval(ctx, actionSource));
        const val1 = getValueFromValueProducer(this._val1.eval(ctx, actionSource));
        const val2 = getValueFromValueProducer(this._val2.eval(ctx, actionSource));
        const val3 = getValueFromValueProducer(this._val3.eval(ctx, actionSource));
        const val4 = definedMap(this._val4, v => getValueFromValueProducer(v.eval(ctx, actionSource)));
        const val5 = definedMap(this._val5, v => getValueFromValueProducer(v.eval(ctx, actionSource)));
        const val6 = definedMap(this._val6, v => getValueFromValueProducer(v.eval(ctx, actionSource)));
        const val7 = definedMap(this._val7, v => getValueFromValueProducer(v.eval(ctx, actionSource)));
        const val8 = definedMap(this._val8, v => getValueFromValueProducer(v.eval(ctx, actionSource)));
        const val9 = definedMap(this._val9, v => getValueFromValueProducer(v.eval(ctx, actionSource)));
        const val10 = definedMap(this._val10, v => getValueFromValueProducer(v.eval(ctx, actionSource)));
        const val11 = definedMap(this._val11, v => getValueFromValueProducer(v.eval(ctx, actionSource)));
        const val12 = definedMap(this._val12, v => getValueFromValueProducer(v.eval(ctx, actionSource)));

        if (
            zapID === LOADING ||
            val1 === LOADING ||
            val2 === LOADING ||
            val3 === LOADING ||
            val4 === LOADING ||
            val5 === LOADING ||
            val6 === LOADING ||
            val7 === LOADING ||
            val8 === LOADING ||
            val9 === LOADING ||
            val10 === LOADING ||
            val11 === LOADING ||
            val12 === LOADING
        ) {
            return undefined;
        }

        if (typeof zapID !== "string" || isEmptyOrUndefinedish(zapID)) {
            return undefined;
        }

        const { appID, appFacilities } = env.appEnvironment;

        const val1String = asBasePrimitiveValue(val1);
        const val2String = asBasePrimitiveValue(val2);
        const val3String = asBasePrimitiveValue(val3);
        const val4String = asBasePrimitiveValue(val4);
        const val5String = asBasePrimitiveValue(val5);
        const val6String = asBasePrimitiveValue(val6);
        const val7String = asBasePrimitiveValue(val7);
        const val8String = asBasePrimitiveValue(val8);
        const val9String = asBasePrimitiveValue(val9);
        const val10String = asBasePrimitiveValue(val10);
        const val11String = asBasePrimitiveValue(val11);
        const val12String = asBasePrimitiveValue(val12);

        const body: TriggerZapBody = {
            zapID,
            appID,
            val1: val1String,
            val2: val2String,
            val3: val3String,
            val4: val4String,
            val5: val5String,
            val6: val6String,
            val7: val7String,
            val8: val8String,
            val9: val9String,
            val10: val10String,
            val11: val11String,
            val12: val12String,
        };
        const resp = await appFacilities.callAuthIfAvailableCloudFunction("triggerZap", body, {});

        const status = resp?.status;
        const success = status === 200;
        appFacilities.trackEvent(success ? "zap sent" : "zap failure", { app_id: appID });
        const callback = status === 402 ? this._onQuotaReached : success ? this._onSuccess : this._onFailure;
        if (callback === undefined) {
            return;
        }

        return (await evalAndRunAction(ctx, env, actionSource, callback)) ?? ActionResult.ZapierTrigger;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "TriggerZapWithValues",
            "action",
            ["zapID", "evaluable"],
            ["val1", "evaluable"],
            ["val2", "evaluable"],
            ["val3", "evaluable"],
            ["val4", "evaluable", false],
            ["val5", "evaluable", false],
            ["val6", "evaluable", false],
            ["val7", "evaluable", false],
            ["val8", "evaluable", false],
            ["val9", "evaluable", false],
            ["val10", "evaluable", false],
            ["val11", "evaluable", false],
            ["val12", "evaluable", false],
            ["onSuccess", "action"],
            ["onFailure", "action"],
            ["onQuotaReached", "action", false],
        ];
    }
}

class TriggerWebhookAction extends OnlineOnlyAction {
    constructor(
        private readonly _webhookID: EnvironmentBoundEvaluable,
        private readonly _namesAndValues: EnvironmentBoundEvaluable,
        private readonly _onSuccess: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        let webhookID: string | undefined;
        const params: WebhookValues = {};

        await withTimeout(
            (async () => {
                const [webhookIDValue, maybeNamesAndValues] = await Promise.all([
                    definedMap(this._webhookID.eval(ctx, actionSource), waitForValueFromValueProducer),
                    definedMap(this._namesAndValues.eval(ctx, actionSource), waitForValueFromValueProducer),
                ]);
                if (!isLoadedDefined(maybeNamesAndValues) || !isLoadedDefined(webhookIDValue)) return;

                webhookID = asString(webhookIDValue);

                const namesAndValues = await Promise.all(
                    asArrayValue(maybeNamesAndValues).map(waitForValueFromValueProducer)
                );
                for (let i = 0; i < namesAndValues.length; i += 2) {
                    const name = loadedDefinedMap(namesAndValues[i + 0], asString);
                    const value = loadedDefinedMap(namesAndValues[i + 1], makeWebhookValue);
                    if (name === undefined || value === undefined) continue;

                    params[name] = value;
                }
            })(),
            undefined,
            undefined,
            actionRunTimeout
        );

        if (webhookID === undefined) {
            env.appEnvironment.showToast(getLocalizedString("error", env.appEnvironment.appKind), false);
            return undefined;
        }

        const body: TriggerAppWebhookActionBody = {
            appID: env.appEnvironment.appID,
            webhookID,
            params,
        };

        const result = await env.appEnvironment.appFacilities.callAuthIfAvailableCloudFunction(
            "triggerAppWebhookAction",
            body,
            {}
        );
        if (result?.ok !== true) {
            env.appEnvironment.showToast(getLocalizedString("error", env.appEnvironment.appKind), false);
            return undefined;
        }

        return await evalAndRunAction(ctx, env, actionSource, this._onSuccess);
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "TriggerWebhook",
            "action",
            ["webhookID", "evaluable"],
            ["namesAndValues", "evaluable"],
            ["onSuccess", "action", false],
        ];
    }
}

class ShowFormSuccessToastAction extends Action {
    constructor(
        private readonly _message: EnvironmentBoundEvaluable | undefined,
        private readonly _isSuccess: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const message = definedMap(this._message?.eval(ctx, actionSource), getValueFromValueProducer);
        const isSuccess = definedMap(this._isSuccess?.eval(ctx, actionSource), getValueFromValueProducer);
        if (message === LOADING || isSuccess === LOADING) return undefined;

        const success = definedMap(isSuccess, asBoolean) ?? true;
        const text =
            definedMap(message, asString) ??
            getLocalizedString(success ? "formSubmissionSuccess" : "error", env.appEnvironment.appKind);

        env.appEnvironment.showToast(text, success);

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["ShowFormSuccessToast", "action", ["message", "evaluable", false], ["isSuccess", "evaluable", false]];
    }
}

class PlaySoundAction extends Action {
    constructor(private readonly _sound: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const sound = definedMap(this._sound.eval(ctx, actionSource), getValueFromValueProducer);

        const soundName = loadedDefinedMap(sound, asString);
        if (soundName === undefined) return undefined;

        // Find soundName in enum type. I didn't want to create an
        // 'asSound' function as it might clutter up the primitive types
        const validSounds = Object.values(Sound);
        const typedSound = validSounds.find(s => s === soundName);
        if (typedSound === undefined) return;

        env.appEnvironment.playSound(typedSound);

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["PlaySound", "action", ["sound", "evaluable", true]];
    }
}

class ShowPopupModalAction extends Action {
    constructor(
        private readonly _title: EnvironmentBoundEvaluable,
        private readonly _description: EnvironmentBoundEvaluable,
        private readonly _acceptText: EnvironmentBoundEvaluable,
        private readonly _cancelText: EnvironmentBoundEvaluable,
        private readonly _style: EnvironmentBoundEvaluable,
        private readonly _onAccept: EnvironmentBoundEvaluable,
        private readonly _onCancel: EnvironmentBoundEvaluable
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const title = loadedDefinedMap(getValueFromValueProducer(this._title.eval(ctx, actionSource)), asString);
        const description = loadedDefinedMap(
            getValueFromValueProducer(this._description.eval(ctx, actionSource)),
            asString
        );
        const acceptText = loadedDefinedMap(
            getValueFromValueProducer(this._acceptText.eval(ctx, actionSource)),
            asString
        );
        const cancelText = loadedDefinedMap(
            getValueFromValueProducer(this._cancelText.eval(ctx, actionSource)),
            asString
        );
        const style = loadedDefinedMap(getValueFromValueProducer(this._style.eval(ctx, actionSource)), asString);
        const onAccept = evalAction(ctx, actionSource, this._onAccept);
        const onCancel = evalAction(ctx, actionSource, this._onCancel);
        if (
            title === undefined ||
            description === undefined ||
            acceptText === undefined ||
            cancelText === undefined ||
            style === undefined ||
            onAccept === undefined ||
            onCancel === undefined
        ) {
            return undefined;
        }

        const result = await showConfirmModal({
            title,
            description,
            accept: acceptText,
            cancel: cancelText,
            style: style as ConfirmModalStyle,
        });

        if (result === true) {
            return onAccept.run(ctx, env, actionSource);
        } else if (result === false) {
            return onCancel.run(ctx, env, actionSource);
        }

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "ShowPopupModal",
            "action",
            ["title", "evaluable"],
            ["description", "evaluable"],
            ["acceptText", "evaluable"],
            ["cancelText", "evaluable"],
            ["style", "evaluable"],
            ["onAccept", "action"],
            ["onCancel", "action"],
        ];
    }
}

class CopyToClipboardAction extends Action {
    constructor(private readonly _message: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const message = getValueFromValueProducer(this._message.eval(ctx, actionSource));
        if (message === LOADING) return undefined;

        env.appEnvironment.copyToClipboard(asString(message));

        return ActionResult.CopiedToClipboard;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["CopyToClipboard", "action", ["message", "evaluable"]];
    }
}

class ShowShareSheetAction extends Action {
    constructor(
        private readonly _message: EnvironmentBoundEvaluable | undefined,
        private readonly _url: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    public async run(
        ctx: Context,
        _env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const [message, url] = await evalAndWaitForValues(ctx, actionSource, [this._message, this._url]);
        if (!isLoadedDefined(message) && !isLoadedDefined(url)) return;

        const nav = navigator as any;
        try {
            logInfo("share sheet", message, url);
            nav.share({
                ...(message !== undefined ? { text: asString(message) } : { url: asString(url) }),
            });
        } catch {
            // do nothing
        }

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["ShowShareSheet", "action", ["message", "evaluable", false], ["url", "evaluable", false]];
    }
}

function evalFlags(
    evaluable: EnvironmentBoundEvaluable | undefined,
    ctx: Context,
    actionSource: ActionSource
): ReadonlySet<ScreenFlag> | undefined {
    if (evaluable === undefined) return undefined;

    const v = getValueFromValueProducer(evaluable.eval(ctx, actionSource));
    if (!isLoadedDefined(v)) return undefined;

    const arr = mapFilterUndefined(asArrayValue(v), x =>
        loadedDefinedMap(getValueFromValueProducer(x), y => asString(y) as ScreenFlag)
    );
    return new Set(arr);
}

abstract class PushScreenAction extends Action {
    constructor(
        protected readonly name: EnvironmentBoundEvaluable,
        protected readonly ctx: EnvironmentBoundEvaluable,
        protected readonly title: EnvironmentBoundEvaluable | undefined,
        protected readonly flags: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    public canExecute(ctx: Context, force: ForceValueProducer): boolean {
        const screenCtx = force(this.ctx.eval(ctx, undefined));
        if (!isLoadedDefined(screenCtx)) {
            return false;
        }
        if (isArray(screenCtx)) {
            return screenCtx.length > 0;
        }
        return true;
    }
}

class PushBuilderScreenAction extends PushScreenAction {
    constructor(
        name: EnvironmentBoundEvaluable,
        ctx: EnvironmentBoundEvaluable,
        title: EnvironmentBoundEvaluable | undefined,
        flags: EnvironmentBoundEvaluable | undefined,
        private readonly _itemID: EnvironmentBoundEvaluable | undefined
    ) {
        super(name, ctx, title, flags);
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const screenCtx = this.ctx.eval(ctx, actionSource);
        if (!isLoadedDefined(screenCtx.current)) return;

        const name = getValueFromValueProducer(this.name.eval(ctx, actionSource));
        if (!isLoadedDefined(name)) return;

        const itemID = loadedDefinedMap(
            definedMap(this._itemID?.eval(ctx, actionSource), getValueFromValueProducer),
            asString
        );

        env.appEnvironment.pushBuilderScreen(
            asString(name),
            screenCtx,
            this.title,
            env,
            evalFlags(this.flags, ctx, actionSource),
            itemID
        );
        return undefined;
    }

    public get autoRunInTabletMode(): boolean {
        return true;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "PushBuilderScreen",
            "action",
            ["name", "evaluable"],
            ["ctx", "evaluable"],
            ["title", "evaluable", false],
            ["flags", "evaluable", false],
            ["itemID", "evaluable", false],
        ];
    }
}

class PushModalBuilderScreenAction extends PushScreenAction {
    constructor(
        name: EnvironmentBoundEvaluable,
        ctx: EnvironmentBoundEvaluable,
        title: EnvironmentBoundEvaluable | undefined,
        flags: EnvironmentBoundEvaluable | undefined,
        private readonly _forEditorComponent: EnvironmentBoundEvaluable | undefined,
        private readonly _reallyNeedsOnline: EnvironmentBoundEvaluable | undefined
    ) {
        super(name, ctx, title, flags);
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const name = getValueFromValueProducer(this.name.eval(ctx, actionSource));
        if (!isLoadedDefined(name)) return;

        env.appEnvironment.pushModalBuilderScreen(
            asString(name),
            this.ctx.eval(ctx, actionSource),
            this.title,
            evalFlags(this.flags, ctx, actionSource)
        );
        return undefined;
    }

    public needsOnline(ctx: Context, force: ForceValueProducer, env: RenderEnvironment): [boolean, boolean] {
        const forEditorValue = definedMap(this._forEditorComponent?.eval(ctx, undefined), force);
        const forEditor =
            forEditorValue === true
                ? true
                : forEditorValue === "offline-action-queue"
                ? !supportsOfflineActionQueue(env.appEnvironment.appFeatures)
                : false;
        const reallyNeedsOnlineValue = definedMap(this._reallyNeedsOnline?.eval(ctx, undefined), force);
        const reallyNeedsOnline = loadedDefinedMap(reallyNeedsOnlineValue, asBoolean) === true;
        return [forEditor, reallyNeedsOnline];
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "PushModalBuilderScreen",
            "action",
            ["name", "evaluable"],
            ["ctx", "evaluable"],
            ["title", "evaluable", false],
            ["flags", "evaluable", false],
            ["forEditComponent", "evaluable", false],
            ["reallyNeedsOnline", "evaluable", false],
        ];
    }
}

// FIXME: Do all the tablet auto-run stuff
class PushMenuScreenAction extends Action {
    constructor(
        private readonly _menuID: EnvironmentBoundEvaluable,
        private readonly _primaryKey: EnvironmentBoundEvaluable,
        private readonly _defaultScreenName: EnvironmentBoundEvaluable,
        private readonly _ctx: EnvironmentBoundEvaluable
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const screenCtx = this._ctx.eval(ctx, actionSource);
        if (!isLoadedDefined(screenCtx.current)) return;

        const menuID = getValueFromValueProducer(this._menuID.eval(ctx, actionSource));
        if (!isLoadedDefined(menuID)) return;

        const primaryKey = getValueFromValueProducer(this._primaryKey.eval(ctx, actionSource));
        if (!isLoadedDefined(primaryKey)) return;

        const defaultScreenName = getValueFromValueProducer(this._defaultScreenName.eval(ctx, actionSource));
        if (!isLoadedDefined(defaultScreenName)) return;

        env.appEnvironment.pushMenuScreen(
            asString(menuID),
            md5(asString(primaryKey)),
            asString(defaultScreenName),
            screenCtx,
            env
        );
        return undefined;
    }

    public canExecute(ctx: Context, force: ForceValueProducer): boolean {
        const screenCtx = force(this._ctx.eval(ctx, undefined));
        if (!isLoadedDefined(screenCtx)) {
            return false;
        }
        if (isArray(screenCtx)) {
            return screenCtx.length > 0;
        }
        return true;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "PushMenuScreen",
            "action",
            ["menuID", "evaluable"],
            ["primaryKey", "evaluable"],
            ["defaultScreenName", "evaluable"],
            ["ctx", "evaluable"],
        ];
    }
}

class PopScreenAction extends Action {
    public async run(_ctx: Context, env: RenderEnvironment): Promise<ActionResult | undefined> {
        env.appEnvironment.popScreen(env);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["PopScreen", "action"];
    }
}

class PopModalScreenAction extends Action {
    public async run(_ctx: Context, env: RenderEnvironment): Promise<ActionResult | undefined> {
        env.appEnvironment.popModalScreen(env);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["PopModalScreen", "action"];
    }
}

class PopAnyScreenAction extends Action {
    public async run(_ctx: Context, env: RenderEnvironment): Promise<ActionResult | undefined> {
        env.appEnvironment.popAnyScreen(env);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["PopAnyScreen", "action"];
    }
}

class SetCurrentTabAction extends Action {
    constructor(private readonly _index: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const index = getValueFromValueProducer(this._index.eval(ctx, actionSource));
        if (index === LOADING) return undefined;
        env.appEnvironment.setCurrentTab(asNumber(index), env);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["SetCurrentTab", "action", ["index", "evaluable"]];
    }
}

class SetCurrentPivotAction extends Action {
    constructor(private readonly _index: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const index = getValueFromValueProducer(this._index.eval(ctx, actionSource));
        if (index === LOADING) return undefined;
        env.appEnvironment.setCurrentPivot(asNumber(index), env);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["SetCurrentPivot", "action", ["index", "evaluable"]];
    }
}

class OpenLinkAction extends Action {
    constructor(private readonly _url: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        _actionSource: ActionSource,
        handled: boolean = false
    ): Promise<ActionResult | undefined> {
        const url = getValueFromValueProducer(this._url.eval(ctx, ActionSource.OpenLink));
        if (url === LOADING || handled) return undefined;
        env.appEnvironment.appFacilities.openURL(asString(url));
        return undefined;
    }

    public canExecute(ctx: Context, force: ForceValueProducer): boolean {
        const url = force(this._url.eval(ctx, ActionSource.OpenLink));
        return isLoadedDefined(url);
    }

    public targetURL(ctx: Context, _env: RenderEnvironment): string | undefined {
        const url = getValueFromValueProducer(this._url.eval(ctx, ActionSource.OpenLink));
        return loadedDefinedMap(url, u => safeURL(asString(u)));
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["OpenLink", "action", ["url", "evaluable"]];
    }
}

class OpenMapAction extends OnlineOnlyAction {
    constructor(
        private readonly _latitude: EnvironmentBoundEvaluable | undefined,
        private readonly _longitude: EnvironmentBoundEvaluable | undefined,
        private readonly _address: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    private getLocation(ctx: Context): MapLocation | undefined {
        if (this._address !== undefined) {
            const address = loadedDefinedMap(
                getValueFromValueProducer(this._address.eval(ctx, ActionSource.OpenLink)),
                asString
            );
            if (address === undefined) return undefined;
            return { kind: MapLocationKind.Address, address };
        } else if (this._latitude !== undefined && this._longitude !== undefined) {
            const latitude = loadedDefinedMap(
                getValueFromValueProducer(this._latitude.eval(ctx, ActionSource.OpenLink)),
                asNumber
            );
            const longitude = loadedDefinedMap(
                getValueFromValueProducer(this._longitude.eval(ctx, ActionSource.OpenLink)),
                asNumber
            );
            if (latitude === undefined || longitude === undefined) return undefined;
            return { kind: MapLocationKind.LatLong, latitude, longitude };
        } else {
            return undefined;
        }
    }

    public targetURL(ctx: Context, env: RenderEnvironment): string | undefined {
        return definedMap(this.getLocation(ctx), l => env.appEnvironment.appFacilities.mapURL(l));
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        _actionSource: ActionSource,
        handled?: boolean
    ): Promise<ActionResult | undefined> {
        if (handled === true) return undefined;

        const location = this.getLocation(ctx);
        if (location === undefined) return;
        env.appEnvironment.appFacilities.openMap(location);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "OpenMap",
            "action",
            ["latitude", "evaluable", false],
            ["longitude", "evaluable", false],
            ["address", "evaluable", false],
        ];
    }
}

class SendEmailAction extends OnlineOnlyAction {
    constructor(
        private readonly _to: EnvironmentBoundEvaluable | undefined,
        private readonly _subject: EnvironmentBoundEvaluable | undefined,
        private readonly _body: EnvironmentBoundEvaluable | undefined,
        private readonly _cc: EnvironmentBoundEvaluable | undefined,
        private readonly _bcc: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    public targetURL(ctx: Context, env: RenderEnvironment): string | undefined {
        const actionSource = ActionSource.SendEmail;
        const to = loadedDefinedMap(
            definedMap(this._to, e => getValueFromValueProducer(e.eval(ctx, actionSource))),
            asString
        );
        const subject = loadedDefinedMap(
            definedMap(this._subject, e => getValueFromValueProducer(e.eval(ctx, actionSource))),
            asString
        );
        const body = loadedDefinedMap(
            definedMap(this._body, e => getValueFromValueProducer(e.eval(ctx, actionSource))),
            asString
        );
        const cc = loadedDefinedMap(
            definedMap(this._cc, e => getValueFromValueProducer(e.eval(ctx, actionSource))),
            asString
        );
        const bcc = loadedDefinedMap(
            definedMap(this._bcc, e => getValueFromValueProducer(e.eval(ctx, actionSource))),
            asString
        );

        const { appEnvironment } = env;
        return env.appEnvironment.appFacilities.mailtoURL(
            getEmailClient(appEnvironment.appFeatures),
            to,
            subject,
            body,
            cc,
            bcc
        );
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        _actionSource: ActionSource,
        handled?: boolean
    ): Promise<ActionResult | undefined> {
        if (handled === true) return undefined;

        const url = this.targetURL(ctx, env);
        if (url === undefined) return undefined;
        env.appEnvironment.appFacilities.openURL(url);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "SendEmail",
            "action",
            ["to", "evaluable", false],
            ["subject", "evaluable", false],
            ["body", "evaluable", false],
            ["cc", "evaluable", false],
            ["bcc", "evaluable", false],
        ];
    }
}

class SetAction extends Action {
    constructor(private readonly _lhs: EnvironmentBoundEvaluable, private readonly _rhs: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        _env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const lhs = this._lhs.eval(ctx, actionSource);
        const rhs = getValueFromValueProducer(this._rhs.eval(ctx, actionSource));
        if (rhs === LOADING) return undefined;
        lhs.set(rhs);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["Set", "action", ["lhs", "evaluable"], ["rhs", "evaluable"]];
    }
}

class SetPropertiesAction extends Action {
    constructor(
        private readonly _lhs: EnvironmentBoundEvaluable,
        private readonly _rhs: EnvironmentBoundEvaluable,
        private readonly _columnNames: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    protected makeValue(v: Value): Value {
        return v;
    }

    public async run(
        ctx: Context,
        _env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const lhs = this._lhs.eval(ctx, actionSource);
        const rhsInspector = getObjectInspectorFromValueProducer(this._rhs.eval(ctx, actionSource));
        const lhsInspector = getObjectInspectorFromValueProducer(lhs);
        if (!isLoadedDefined(lhsInspector) || !isLoadedDefined(rhsInspector)) return undefined;

        const columnNamesSet = evaluateStringArray(this._columnNames, ctx, actionSource);
        if (columnNamesSet === LOADING) return undefined;

        const rhs = rhsInspector.getObjectValue();
        for (const k of Object.keys(rhs)) {
            if (columnNamesSet !== undefined && !columnNamesSet.has(k)) continue;

            const lhsVP = lhsInspector.getProperty(k);
            const rhsValue = getValueFromValueProducer(defined(rhs[k]));

            if (rhsValue === LOADING) {
                logError(`RHS property ${k} isn't loaded`);
                continue;
            }

            if (rhsValue === undefined || rhsValue === "") {
                if (lhsVP !== undefined) {
                    lhs.removeProperty(k);
                }
            } else if (lhsVP === undefined) {
                lhs.addProperty(k, new Data(this.makeValue(rhsValue)));
            } else {
                lhsVP.set(this.makeValue(rhsValue));
            }
        }
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "SetProperties",
            "action",
            ["lhs", "evaluable"],
            ["rhs", "evaluable"],
            ["columnNames", "evaluable", false],
        ];
    }
}

class SetStringPropertiesAction extends SetPropertiesAction {
    protected makeValue(v: Value): Value {
        return asString(v);
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["SetStringProperties", "action", ["lhs", "evaluable"], ["rhs", "evaluable"]];
    }
}

class SetNumberPropertiesAction extends SetPropertiesAction {
    protected makeValue(v: Value): Value {
        return Number.parseFloat(asString(v));
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["SetNumberProperties", "action", ["lhs", "evaluable"], ["rhs", "evaluable"]];
    }
}

class PostCommentAction extends OnlineOnlyAction {
    constructor(private readonly _topic: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(ctx: Context, env: RenderEnvironment): Promise<ActionResult | undefined> {
        const topic = getValueFromValueProducer(this._topic.eval(ctx, ActionSource.PostComment));
        if (!isLoadedDefined(topic)) return undefined;

        const inspector = getObjectInspectorFromValueProducer(ctx);
        if (!isLoadedDefined(inspector)) return undefined;

        const username = loadedDefinedMap(
            definedMap(inspector.getProperty("username"), getValueFromValueProducer),
            asString
        );
        const email = loadedDefinedMap(definedMap(inspector.getProperty("email"), getValueFromValueProducer), asString);
        const comment = loadedDefinedMap(
            definedMap(inspector.getProperty("comment"), getValueFromValueProducer),
            asString
        );

        if (username === undefined || comment === undefined) return undefined;

        const { database } = env.appEnvironment;
        const document: DocumentData = {
            topic: asString(topic),
            username,
            comment,
            dateTime: { $dateTime: new Date(), $isTimeZoneAgnostic: false },
        };
        if (email !== undefined) {
            document.email = normalizeEmailAddress(email);
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        database.setDocument(makeCommentsPath(env.appEnvironment.appID), undefined, document);

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["PostComment", "action", ["topic", "evaluable"]];
    }
}

class DeleteCommentsAction extends OnlineOnlyAction {
    constructor(private readonly _topic: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(ctx: Context, env: RenderEnvironment): Promise<ActionResult | undefined> {
        const topic = getValueFromValueProducer(this._topic.eval(ctx, ActionSource.PostComment));
        if (!isLoadedDefined(topic)) return undefined;

        const documentIDsValue = asArrayValue(getValueFromValueProducer(ctx));
        const documentIDs = mapFilterUndefined(documentIDsValue, vp =>
            loadedDefinedMap(getValueFromValueProducer(vp), checkString)
        );
        const rowIndexes = documentIDs.map(id => ({ keyColumnName: documentIDColumnName, keyColumnValue: id }));

        const { appEnvironment } = env;
        const {
            appID,
            appFacilities,
            database,
            authenticator: { appUserID },
        } = appEnvironment;

        const authUserID = await appFacilities.getAuthUserID();

        await database.runBatch("postDeleteComments", async batch => {
            for (const rowIndex of rowIndexes) {
                await postDeleteRowAction(
                    batch,
                    appID,
                    authUserID,
                    appUserID,
                    undefined,
                    commentsTableName,
                    rowIndex,
                    env.appEnvironment.isBuilder,
                    false
                );
            }
        });

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["DeleteComments", "action", ["topic", "evaluable"]];
    }
}

class AppendItemAction extends Action {
    constructor(private readonly _array: EnvironmentBoundEvaluable, private readonly _item: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        _env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const array = this._array.eval(ctx, actionSource);
        const item = this._item.eval(ctx, actionSource);

        array.appendItem(item);

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["AppendItem", "action", ["array", "evaluable"], ["item", "evaluable"]];
    }
}

class RemoveItemsAction extends Action {
    constructor(
        private readonly _array: EnvironmentBoundEvaluable,
        private readonly _predicate: EnvironmentBoundEvaluable
    ) {
        super();
    }

    public async run(
        ctx: Context,
        _env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const arrayVP = this._array.eval(ctx, actionSource);
        const array = getValueFromValueProducer(arrayVP);

        if (!isLoadedDefined(array)) return;

        if (!isArray(array)) {
            return panic("Trying to remove items from non-array");
        }

        const len = array.length;
        for (let i = len - 1; i >= 0; i--) {
            const shouldRemove = getValueFromValueProducer(this._predicate.eval(array[i], actionSource));
            if (!isLoadedDefined(shouldRemove) || !asBoolean(shouldRemove)) continue;
            arrayVP.removeItemAtIndex(i);
        }

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["RemoveItems", "action", ["array", "evaluable"], ["predicate", "evaluable"]];
    }
}

class RemoveItemFromParentAction extends Action {
    constructor(private readonly _item: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        _env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const item = this._item.eval(ctx, actionSource);
        item.removeFromParent();
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["RemoveItemFromParent", "action", ["item", "evaluable"]];
    }
}

class AddRowToTableAction extends Action {
    constructor(
        private readonly _tableName: EnvironmentBoundEvaluable,
        private readonly _columnValues: EnvironmentBoundEvaluable,
        private readonly _columnNames: EnvironmentBoundEvaluable | undefined,
        private readonly _addToData: EnvironmentBoundEvaluable | undefined,
        private readonly _onWriteSuccess: EnvironmentBoundEvaluable | undefined,
        private readonly _dataStoreName: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    public needsOnline(_context: Context, _force: ForceValueProducer, env: RenderEnvironment): [boolean, boolean] {
        return [!supportsOfflineActionQueue(env.appEnvironment.appFeatures), false];
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        function showError() {
            env.appEnvironment.showToast(getLocalizedString("error", env.appEnvironment.appKind), false);
        }

        try {
            const tableName = getValueFromValueProducer(this._tableName.eval(ctx, actionSource));
            if (!isLoadedDefined(tableName)) {
                logError("Add row to table: no table name");
                showError();
                return undefined;
            }

            const columnValues = this._columnValues.eval(ctx, actionSource);

            const columnNamesSet = evaluateStringArray(this._columnNames, ctx, actionSource);
            if (columnNamesSet === LOADING) {
                logError("Add row to table: column names set is loading");
                showError();
                return undefined;
            }

            const addToData = loadedDefinedMap(
                definedMap(this._addToData, a => getValueFromValueProducer(a.eval(ctx, actionSource))),
                checkBoolean
            );

            const dataStoreName = loadedDefinedMap(
                definedMap(this._dataStoreName?.eval(ctx, actionSource), getValueFromValueProducer),
                asString
            );

            logInfo("Add row to table", dataStoreName, tableName, columnValues, columnNamesSet, addToData);

            let dataStore: DataStore;
            if (dataStoreName === "local") {
                dataStore = env.appEnvironment.localDataStore;
            } else {
                dataStore = env.appEnvironment.dataStore;
            }

            const { rowVP: newRow, didAdd } = await dataStore.addRowToTable(
                makeTableName(checkString(tableName)),
                columnValues,
                columnNamesSet,
                addToData === true,
                false
            );
            if (didAdd) {
                // We pass in the new row to the callback here, which means
                // that we can't use the callback to sequence actions.  The
                // follow-on actions also need the context.  Luckily our
                // actions are async now, so sequencing works by default.  We
                // do use this callback for on-submit actions.
                //
                // FIXME: It's almost certainly wrong to pass in either the
                // new row or the current context, since they can be of
                // different types, and the callback won't know.
                return await evalAndRunAction(newRow ?? ctx, env, actionSource, this._onWriteSuccess);
            } else {
                showError();
                return undefined;
            }
        } catch {
            showError();
        }

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "AddRowToTable",
            "action",
            ["tableName", "evaluable"],
            ["columnValues", "evaluable"],
            ["columnNames", "evaluable", false],
            ["addToData", "evaluable", false],
            ["onWriteSuccess", "action", false],
            ["dataStoreName", "evaluable", false],
        ];
    }
}

abstract class RowAction extends OnlineOnlyAction {
    constructor(
        private readonly _allowMultipleRows: boolean,
        private readonly _tableName: EnvironmentBoundEvaluable,
        private readonly _rowIndex: EnvironmentBoundEvaluable | undefined,
        private readonly _keyColumnName: EnvironmentBoundEvaluable | undefined,
        private readonly _keyColumnValue: EnvironmentBoundEvaluable | undefined,
        private readonly _setUnderlyingData: EnvironmentBoundEvaluable | undefined
    ) {
        super();
    }

    private makeRowIndexes(ctx: Context, actionSource: ActionSource): ReadonlyArray<RowIndex> | undefined {
        let rowIndexNumber: BaseRowIndex | undefined;
        const rowIndexValue = definedMap(this._rowIndex, e => getValueFromValueProducer(e.eval(ctx, actionSource)));
        if (isLoadedDefined(rowIndexValue)) {
            if (Array.isArray(rowIndexValue)) {
                const rowIndexes: RowIndex[] = [];
                for (const vp of rowIndexValue) {
                    const inspector = getObjectInspectorFromValueProducer(vp);
                    if (!isLoadedDefined(inspector)) continue;
                    const n = definedMap(inspector.getProperty("keyColumnName"), getValueFromValueProducer);
                    const v = definedMap(inspector.getProperty("keyColumnValue"), getValueFromValueProducer);
                    if (!isLoadedDefined(n) || !isLoadedDefined(v)) continue;

                    rowIndexes.push({ keyColumnName: checkString(n), keyColumnValue: v });
                }

                if (!this._allowMultipleRows) {
                    assert(rowIndexes.length === 1);
                }
                return rowIndexes;
            } else {
                rowIndexNumber = checkBaseRowIndex(rowIndexValue);
            }
        }

        let rowIndex: RowIndex;

        const keyColumnName = definedMap(this._keyColumnName, e =>
            getValueFromValueProducer(e.eval(ctx, actionSource))
        );
        const keyColumnValue = definedMap(this._keyColumnValue, e =>
            getValueFromValueProducer(e.eval(ctx, actionSource))
        );
        if (isLoadedDefined(keyColumnName) && isLoadedDefined(keyColumnValue)) {
            rowIndex = { keyColumnName: checkString(keyColumnName), keyColumnValue };
            if (rowIndexNumber !== undefined) {
                rowIndex = { ...rowIndex, rowIndexHint: rowIndexNumber };
            }
        } else {
            if (rowIndexNumber === undefined) return undefined;
            rowIndex = rowIndexNumber;
        }

        return [rowIndex];
    }

    protected abstract runForRows(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource,
        tableName: TableName,
        rowIndex: readonly RowIndex[],
        setUnderlyingData: boolean
    ): Promise<void>;

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const tableName = getValueFromValueProducer(this._tableName.eval(ctx, actionSource));
        if (!isLoadedDefined(tableName)) return undefined;

        const rowIndexes = this.makeRowIndexes(ctx, actionSource);
        if (rowIndexes === undefined) return undefined;

        const setUnderlyingData = definedMap(
            definedMap(this._setUnderlyingData, e => getValueFromValueProducer(e.eval(ctx, actionSource))),
            asBoolean
        );

        await this.runForRows(
            ctx,
            env,
            actionSource,
            makeTableName(checkString(tableName)),
            rowIndexes,
            setUnderlyingData === true
        );

        return undefined;
    }
}

class SetColumnsInRowAction extends RowAction {
    constructor(
        tableName: EnvironmentBoundEvaluable,
        rowIndex: EnvironmentBoundEvaluable | undefined,
        keyColumnName: EnvironmentBoundEvaluable | undefined,
        keyColumnValue: EnvironmentBoundEvaluable | undefined,
        private readonly _columnValues: EnvironmentBoundEvaluable,
        private readonly _debounce: EnvironmentBoundEvaluable | undefined,
        setUnderlyingData: EnvironmentBoundEvaluable | undefined
    ) {
        super(false, tableName, rowIndex, keyColumnName, keyColumnValue, setUnderlyingData);
    }

    protected async runForRows(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource,
        tableName: TableName,
        [rowIndex]: readonly RowIndex[],
        setUnderlyingData: boolean
    ): Promise<void> {
        const columnValues = this._columnValues.eval(ctx, actionSource);
        const withDebounce = definedMap(this._debounce, e => getValueFromValueProducer(e.eval(ctx, actionSource)));

        await env.appEnvironment.dataStore.setColumnsInRow(
            tableName,
            rowIndex,
            columnValues,
            setUnderlyingData,
            withDebounce !== false,
            false
        );
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "SetColumnsInRow",
            "action",
            ["tableName", "evaluable"],
            ["rowIndex", "evaluable", false],
            ["keyColumnName", "evaluable", false],
            ["keyColumnValue", "evaluable", false],
            ["columnValues", "evaluable"],
            ["debounce", "evaluable", false],
            ["setUnderlyingData", "evaluable", false],
        ];
    }
}

class DropImageAction extends RowAction {
    constructor(
        tableName: EnvironmentBoundEvaluable,
        private readonly _columnName: EnvironmentBoundEvaluable,
        rowIndex: EnvironmentBoundEvaluable,
        keyColumnName: EnvironmentBoundEvaluable | undefined,
        keyColumnValue: EnvironmentBoundEvaluable | undefined
    ) {
        super(false, tableName, rowIndex, keyColumnName, keyColumnValue, undefined);
    }

    protected async runForRows(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource,
        tableName: TableName,
        [rowIndex]: readonly RowIndex[]
    ): Promise<void> {
        const { appID, appFacilities, dataStore } = env.appEnvironment;

        const columnName = loadedDefinedMap(
            getValueFromValueProducer(this._columnName.eval(ctx, actionSource)),
            checkString
        );
        const dropData = loadedDefinedMap(getValueFromValueProducer(ctx), s => extractDropData(checkString(s)));

        if (columnName === undefined || dropData === undefined) {
            return;
        }

        const continueWithImageUrl = async (imageUrl: string) => {
            await dataStore.setColumnsInRow(
                tableName,
                rowIndex,
                Data.fromJSON<AppOther, AppEnvironment>({
                    [columnName]: imageUrl,
                }),
                true,
                false,
                true
            );
        };

        switch (dropData.kind) {
            case "uri":
                await continueWithImageUrl(dropData.uri);
                break;
            case "file":
                const { file } = dropData;
                // FIXME: Use the progress indicator
                // FIXME: Support retrying failed uploads
                // FIXME: Support cancelling uploads
                const session = uploadFileIntoGlideStorage(appFacilities, appID, file, "drop-image", undefined);
                const uploadResponse = await session.attempt();
                if (isUploadFileResponseError(uploadResponse)) {
                    // FIXME: Show an error message whenever this happens
                    logError("Could not upload image", uploadResponse.error);
                } else {
                    await continueWithImageUrl(uploadResponse.path);
                }
                break;
        }
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "DropImage",
            "action",
            ["tableName", "evaluable"],
            ["columnName", "evaluable"],
            ["rowIndex", "evaluable"],
            ["keyColumnName", "evaluable", false],
            ["keyColumnValue", "evaluable", false],
        ];
    }
}

class DeleteRowAction extends RowAction {
    constructor(
        tableName: EnvironmentBoundEvaluable,
        rowIndex: EnvironmentBoundEvaluable | undefined,
        keyColumnName: EnvironmentBoundEvaluable | undefined,
        keyColumnValue: EnvironmentBoundEvaluable | undefined,
        setUnderlyingData: EnvironmentBoundEvaluable | undefined
    ) {
        super(true, tableName, rowIndex, keyColumnName, keyColumnValue, setUnderlyingData);
    }

    protected async runForRows(
        _ctx: Context,
        env: RenderEnvironment,
        _actionSource: ActionSource,
        tableName: TableName,
        [rowIndex]: readonly RowIndex[],
        setUnderlyingData: boolean
    ): Promise<void> {
        await env.appEnvironment.dataStore.deleteRowAtIndex(tableName, rowIndex, setUnderlyingData, false);
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "DeleteRow",
            "action",
            ["tableName", "evaluable"],
            ["rowIndex", "evaluable", false],
            ["keyColumnName", "evaluable", false],
            ["keyColumnValue", "evaluable", false],
            ["setUnderlyingData", "evaluable", false],
        ];
    }
}

class DeleteRowFromTableAction extends OnlineOnlyAction {
    constructor(
        private readonly _dataStoreName: EnvironmentBoundEvaluable,
        private readonly _tableName: EnvironmentBoundEvaluable,
        private readonly _row: EnvironmentBoundEvaluable
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const tableName = getValueFromValueProducer(this._tableName.eval(ctx, actionSource));
        if (!isLoadedDefined(tableName)) return undefined;

        const dataStoreName = loadedDefinedMap(
            definedMap(this._dataStoreName?.eval(ctx, actionSource), getValueFromValueProducer),
            asString
        );

        let dataStore: DataStore;
        if (dataStoreName === "local") {
            dataStore = env.appEnvironment.localDataStore;
        } else {
            dataStore = env.appEnvironment.dataStore;
        }

        const row = this._row.eval(ctx, actionSource);
        if (!isLoadedDefined(row)) return undefined;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        dataStore.deleteRow(makeTableName(checkString(tableName)), row, false);

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return [
            "DeleteRowFromTable",
            "action",
            ["dataStoreName", "evaluable"],
            ["tableName", "evaluable"],
            ["row", "evaluable"],
        ];
    }
}

class ClearTableAction extends OnlineOnlyAction {
    constructor(
        private readonly _dataStoreName: EnvironmentBoundEvaluable,
        private readonly _tableName: EnvironmentBoundEvaluable
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const tableName = getValueFromValueProducer(this._tableName.eval(ctx, actionSource));
        if (!isLoadedDefined(tableName)) return undefined;

        const dataStoreName = loadedDefinedMap(
            definedMap(this._dataStoreName?.eval(ctx, actionSource), getValueFromValueProducer),
            asString
        );

        let dataStore: DataStore;
        if (dataStoreName === "local") {
            dataStore = env.appEnvironment.localDataStore;
        } else {
            dataStore = env.appEnvironment.dataStore;
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        dataStore.clearTable(makeTableName(checkString(tableName)));

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["ClearTable", "action", ["dataStoreName", "evaluable"], ["tableName", "evaluable"]];
    }
}

class ConfirmActionAction extends Action {
    constructor(private readonly _result: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        _env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const result = getValueFromValueProducer(this._result.eval(ctx, actionSource));
        if (!isLoadedDefined(result)) return undefined;

        return asString(result) as ActionResult;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["ConfirmAction", "action", ["result", "evaluable"]];
    }
}

class SignOutAction extends Action {
    public async run(_ctx: Context, { appEnvironment }: RenderEnvironment): Promise<ActionResult | undefined> {
        if (appEnvironment.isBuilder) return undefined;

        appEnvironment.signOut();
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["SignOut", "action"];
    }
}

class ReshuffleAction extends Action {
    public async run(ctx: Context): Promise<ActionResult | undefined> {
        reshuffleKeys(ctx);
        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["Reshuffle", "action"];
    }
}

class SendAppFeedbackAction extends OnlineOnlyAction {
    constructor(
        private readonly _from: EnvironmentBoundEvaluable,
        private readonly _message: EnvironmentBoundEvaluable
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const from = getValueFromValueProducer(this._from.eval(ctx, actionSource));
        if (!isLoadedDefined(from)) return undefined;

        const message = getValueFromValueProducer(this._message.eval(ctx, actionSource));
        if (!isLoadedDefined(message)) return undefined;

        const body: SendAppFeedbackBody = {
            appID: env.appEnvironment.appID,
            from: asString(from),
            message: asString(message),
        };
        dontAwait(callCloudFunction(env.appEnvironment.appFacilities.locationSettings, "sendAppFeedback", body));

        env.appEnvironment.appFacilities.trackEvent("feedback sent", { app_id: env.appEnvironment.appID });

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["SendAppFeedback", "action", ["from", "evaluable"], ["message", "evaluable"]];
    }
}

class ReportAppAction extends OnlineOnlyAction {
    constructor(
        private readonly _from: EnvironmentBoundEvaluable,
        private readonly _message: EnvironmentBoundEvaluable,
        private readonly _reason: EnvironmentBoundEvaluable
    ) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const from = getValueFromValueProducer(this._from.eval(ctx, actionSource));
        if (!isLoadedDefined(from)) return undefined;

        const message = getValueFromValueProducer(this._message.eval(ctx, actionSource));
        if (!isLoadedDefined(message)) return undefined;

        const reason = getValueFromValueProducer(this._reason.eval(ctx, actionSource));
        if (!isLoadedDefined(reason)) return undefined;

        const body: ReportAppBody = {
            appID: env.appEnvironment.appID,
            from: asString(from),
            message: asString(message),
            reason: asString(reason),
        };
        dontAwait(callCloudFunction(env.appEnvironment.appFacilities.locationSettings, "reportApp", body));

        env.appEnvironment.appFacilities.trackEvent("report sent", { app_id: env.appEnvironment.appID });

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["ReportApp", "action", ["from", "evaluable"], ["message", "evaluable"], ["reason", "evaluable"]];
    }
}

class OnNotifyConditionVariableAction extends Action {
    constructor(private readonly _cv: EnvironmentBoundEvaluable, private readonly _action: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const cv = loadedDefinedMap(getValueFromValueProducer(this._cv.eval(ctx, actionSource)), asConditionVariable);
        const action = evalAction(ctx, actionSource, this._action);
        if (cv === undefined || action === undefined) return undefined;

        dontAwait(cv.wait().then(() => action.run(ctx, env, actionSource)));

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["OnNotifyConditionVariable", "action", ["cv", "evaluable"], ["action", "evaluable"]];
    }
}

class NotifyConditionVariable extends Action {
    constructor(private readonly _cv: EnvironmentBoundEvaluable) {
        super();
    }

    public async run(
        ctx: Context,
        _env: RenderEnvironment,
        actionSource: ActionSource
    ): Promise<ActionResult | undefined> {
        const cv = loadedDefinedMap(getValueFromValueProducer(this._cv.eval(ctx, actionSource)), asConditionVariable);
        if (cv === undefined) return undefined;

        cv.notifyAll();

        return undefined;
    }

    static get spec(): DeserializationSpec<"action"> {
        return ["NotifyConditionVariable", "action", ["cv", "evaluable"]];
    }
}

export function registerActions(): void {
    registerActionDeserializable(NoAction);
    registerActionDeserializable(HoldAction);
    registerActionDeserializable(SequenceAction);
    registerActionDeserializable(AlertAction);
    registerActionDeserializable(ZapierAction);
    registerActionDeserializable(ShowFormSuccessToastAction);
    registerActionDeserializable(TriggerWebhookAction);
    registerActionDeserializable(PlaySoundAction);
    registerActionDeserializable(ShowPopupModalAction);
    registerActionDeserializable(CopyToClipboardAction);
    registerActionDeserializable(ShowShareSheetAction);
    registerActionDeserializable(PushBuilderScreenAction);
    registerActionDeserializable(PushModalBuilderScreenAction);
    registerActionDeserializable(PushMenuScreenAction);
    registerActionDeserializable(PopScreenAction);
    registerActionDeserializable(PopModalScreenAction);
    registerActionDeserializable(PopAnyScreenAction);
    registerActionDeserializable(SetCurrentTabAction);
    registerActionDeserializable(SetCurrentPivotAction);
    registerActionDeserializable(OpenLinkAction);
    registerActionDeserializable(OpenMapAction);
    registerActionDeserializable(SendEmailAction);
    registerActionDeserializable(SetAction);
    registerActionDeserializable(SetPropertiesAction);
    registerActionDeserializable(SetStringPropertiesAction);
    registerActionDeserializable(SetNumberPropertiesAction);
    registerActionDeserializable(PostCommentAction);
    registerActionDeserializable(DeleteCommentsAction);
    registerActionDeserializable(DropImageAction);
    registerActionDeserializable(AppendItemAction);
    registerActionDeserializable(RemoveItemsAction);
    registerActionDeserializable(RemoveItemFromParentAction);
    registerActionDeserializable(AddRowToTableAction);
    registerActionDeserializable(SetColumnsInRowAction);
    registerActionDeserializable(DeleteRowAction);
    registerActionDeserializable(DeleteRowFromTableAction);
    registerActionDeserializable(ClearTableAction);
    registerActionDeserializable(ConfirmActionAction);
    registerActionDeserializable(SignOutAction);
    registerActionDeserializable(ReshuffleAction);
    registerActionDeserializable(SendAppFeedbackAction);
    registerActionDeserializable(ReportAppAction);
    registerActionDeserializable(OnNotifyConditionVariableAction);
    registerActionDeserializable(NotifyConditionVariable);
    registerActionDeserializable(DeliverEmailAction);
}
